import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, Flex, Select, Spinner, Text, TextField, useToast, TagBadge } from '@chatsdet/ui';
import { usePreviewUrlSource, useCreateSource } from '../api';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';

type ImportUrlDialogProps = {
  onClose: () => void;
};

type PreviewData = {
  format: string;
  label: string;
  endpointCount: number;
  baseUrl: string;
  sourceUrl: string;
  suggestedName: string | null;
};

export const ImportUrlDialog: React.FC<ImportUrlDialogProps> = ({ onClose }) => {
  const [url, setUrl] = React.useState('');
  const [step, setStep] = React.useState<'url' | 'confirm'>('url');
  const [preview, setPreview] = React.useState<PreviewData | null>(null);
  const [name, setName] = React.useState('');
  const [baseUrl, setBaseUrl] = React.useState('');
  const { toast } = useToast();

  const previewMutation = usePreviewUrlSource();
  const createMutation = useCreateSource();
  const { propertyId, selectProperty, propertyOptions, propertiesQuery } = useOrgPropertySelection();

  const handleDetect = React.useCallback(() => {
    if (!url.trim()) return;
    previewMutation.mutate(url.trim(), {
      onSuccess: (result) => {
        if (result.data) {
          setPreview(result.data);
          setBaseUrl(result.data.baseUrl);
          setName(result.data.suggestedName || result.data.label);
          setStep('confirm');
        }
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to detect schema');
      },
    });
  }, [url, previewMutation, toast]);

  const handleCreate = React.useCallback(() => {
    if (!preview || !propertyId) return;
    createMutation.mutate(
      {
        url: preview.sourceUrl,
        propertyId,
        formatId: preview.format,
        formatLabel: preview.label,
        baseUrl: baseUrl.trim() || preview.baseUrl,
        name: name.trim() || preview.label,
      },
      {
        onSuccess: (result) => {
          const data = result.data;
          if (data) {
            toast.success(
              `Added source with ${data.import.imported} endpoint${data.import.imported !== 1 ? 's' : ''}` +
                (data.import.skipped > 0 ? ` (${data.import.skipped} skipped)` : '')
            );
          }
          onClose();
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Import failed');
        },
      }
    );
  }, [preview, propertyId, baseUrl, name, createMutation, toast, onClose]);

  const title = step === 'url' ? 'Add API Source' : 'Confirm Import';
  const description =
    step === 'url'
      ? 'Paste a URL that returns a JSON API schema. We\'ll detect the format and import all endpoints.'
      : 'Review the detected schema and confirm to add this source.';

  return (
    <Dialog title={title} description={description}>
      <DialogContent className="space-y-4">
        {step === 'url' ? (
          <>
            <TextField
              label="Schema URL"
              value={url}
              onChange={setUrl}
              placeholder="https://app.example.io/api/1.1/meta"
              autoFocus
            />
            <Select
              label="Application"
              items={propertyOptions}
              selectedKey={propertyId}
              onSelectionChange={(key) => selectProperty(key as string)}
              placeholder="Select a application..."
              isDisabled={propertiesQuery.isLoading}
            />
          </>
        ) : preview ? (
          <>
            <Flex align="center" gap="sm">
              <TagBadge name={preview.label} color="gray" hideDot />
              <Text size="sm" className="text-muted-foreground">
                {preview.endpointCount} endpoint{preview.endpointCount !== 1 ? 's' : ''} detected
              </Text>
            </Flex>
            <TextField
              label="Source name"
              value={name}
              onChange={setName}
              placeholder={preview.label}
            />
            <TextField
              label="Base URL"
              value={baseUrl}
              onChange={setBaseUrl}
              placeholder={preview.baseUrl}
            />
            <Text size="sm" className="text-muted-foreground">
              Endpoints will be constructed relative to this base URL.
              You can refresh this source later to sync changes.
            </Text>
          </>
        ) : null}
      </DialogContent>
      <DialogFooter>
        {step === 'confirm' && (
          <Button variant="ghost" onPress={() => setStep('url')} className="mr-auto">
            Back
          </Button>
        )}
        <Button variant="secondary" onPress={onClose}>
          Cancel
        </Button>
        {step === 'url' ? (
          <Button
            variant="primary"
            onPress={handleDetect}
            isDisabled={!url.trim() || !propertyId || previewMutation.isPending}
          >
            {previewMutation.isPending ? (
              <Flex align="center" gap="xs">
                <Spinner size="sm" />
                Detecting...
              </Flex>
            ) : (
              'Detect'
            )}
          </Button>
        ) : (
          <Button
            variant="primary"
            onPress={handleCreate}
            isDisabled={!propertyId || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Flex align="center" gap="xs">
                <Spinner size="sm" />
                Importing...
              </Flex>
            ) : (
              'Add Source'
            )}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
